import { MAT_GLYPH, LIFE_GLYPH, BOOK_GLYPH, CARRY_GLYPH, WING_GLYPH, IMPRINT_GLYPH, POT_GLYPH, SCROLL_GLYPH } from '../item-glyphs.js';
/**
 * 가방 — **아이콘 격자**.
 *
 * 목록형은 줄이 길어지면 살펴보기가 안 된다. 장비가 열 자루쯤 쌓이면
 * "내가 뭘 갖고 있더라" 를 스크롤로 찾게 되는데, 그건 가방이 아니라 표다
 * (사용자 지적 "목록형이 아니라 아이콘형처럼").
 *
 * 칸을 누르면 **화면 바닥에 붙는 자리**에 상세와 행동이 열린다 —
 * 모든 줄에 버튼을 늘어놓지 않아도 되고, 어디까지 스크롤했든 손이 닿는다.
 * (처음엔 "아래에" 열었는데, 그 아래가 폰에서는 화면 밖이었다.)
 */
import { S, esc } from '../../core/state.js';
import { alignmentSummary } from '../alignment.js';
import { levelGrowthSummary } from '../level-growth.js';
import { personSprite } from '../../render/person-pixel.js';
import { SLOT_KINDS, SLOT_META, SLOT_NAME, wearSlotsOf } from '../../world/gearnames.js';
import {
  BSTONE_KEY, JUNK_PRICE, POTIONS, PASSIVE_BY_KEY,
  passiveBookSourceText, passiveRankOf, SHARD_KEY, STATS, AUTO_STAT_TARGETS, GRADES, gradeOf, sellPrice,
  CARRY_BY_KEY, carryBonus, carryRankOf, ACC_BY_ID,
  LIFE_STONE_KEY, WING_BY_KEY, PARDON_BY_KEY, GUARD_SHARD_KEY, GUARD_SHARD_NAME,
  SKILL_IMPRINT_BY_KEY, skillImprintRankOf, floorAt, floorLabel, zoneAt, ZONE_KO,
} from '../../world/map.js';
import { LIFE_STONE_SECS, LIFE_STONE_LEVELS } from '../../world/cycle.js';
import { gearTradeLabel, gearTradeRestriction } from '../gear-trade-label.js';
import { equipmentComparison } from '../equipment-compare.js';
import { nativeActionAllowed } from '../../core/net.js';
import { nativeFieldFloor, nativeFieldGeometry, nativeFieldZone } from '../../world/native-field.js';
import { ACTION_KEYS, keyCap } from '../../core/keymap.js';
// 칸과 장비 아이콘은 **대장간과 같은 것을 쓴다**(`common.js`). 같은 물건이
// 화면마다 다르게 생기면 같은 것인 줄 모른다 — 여기 있던 사본을 옮겼다.
import { buyBtn, cell, eqName, eqTitle, equipmentBonus, gearGlyph, gearTone, guildRingInfo, templeRingInfo, isTempleRingItem, muted, num, rollDesc, sec, split , junkName,
  afterTax, taxPct, junkSalePreview, fold, canSellGear, sellGearBtn } from './common.js';

/** UNI-Q192 — 데리고 다닐 수 있는 수의 이유(서버 `bodyguardCapOf` 의 why). 값은 서버가 준다 */
const BG_WHY = { operator: '운영자', rank1: '랭킹 1위', rank2: '랭킹 2위', rank3: '랭킹 3위', base: '기본', off: '지금은 꺼져 있다' };

/**
 * UNI-Q192 — 보디가드 줄. 조각이 다 떨어져도 보디가드가 있으면 여기서 보고 내보낸다.
 * HP·대기(한도 밖)·한도는 서버 값(`y.bodyguard`)을 그리기만 한다.
 * Q276 — 자리는 메뉴 › 나 › 동료(`companions.js`)다. 전에는 가방 세 칸 맨 위였다(메뉴 안에서 한 기능은 한 곳).
 */
export function bodyguardSec(y) {
  const b = y.bodyguard;
  if (!b?.list?.length) return '';
  const active = b.list.filter((g) => g.active).length;
  const rested = b.list.filter((g) => g.resting).length;
  /**
   * UNI-Q210 소환 · 소환 해제 — 사용자: *"보디가드도 소환 및 소환 해제? 기능이 있었으면 좋겠어."*
   * 그리고 *"보디가드 소환 해제는 각 개체마다 하는거 맞지?"* — **줄마다** 버튼이 붙는다(첫 판은 전체 한 번에였다).
   * 「내보내기」(영구 해고)만 있어서 잠시 물려 두려면 영영 잃어야 했다. 이건 기록을 그대로 두고 몸만 물린다.
   */
  /**
   * F24(14.531) — 줄마다 **역할(근접/궁수)·강화 단계·다음 강화 값·부활 값**을 그린다. 값은 전부 서버(`list[].next`·`revive`)가 준다 —
   * 화면이 수식(조각 10×N · 은화 1천만×N)을 복제하지 않는다. 강화·부활은 창고 은화를 함께 쓰는 원천 저장이라 사냥터에서는 잠긴다.
   * F16 이 남긴 「궁수/근접 표시 없음」도 여기서 같이 닫는다(역할은 고용할 때 정해진 기록값).
   */
  const inTown = nativeActionAllowed('bodyguardEnhance');
  const shardsHave = Math.max(0, Math.floor(Number(y.inv?.junk?.[GUARD_SHARD_KEY]) || 0));
  const silverHave = Math.max(0, Math.floor(Number(y.silver) || 0));
  const rows = b.list.map((g, i) => {
    const id = String(g.id).replace(/[^0-9a-z-]/gi, '');
    const lv = Math.max(0, Math.floor(Number(g.lv) || 0));
    const role = g.kind === 'ranged' ? '궁수' : '근접';
    const note = g.down ? ` <small>쓰러짐 — ${g.revive != null ? `부활 ${num(g.revive)} 은화` : '부활할 수 없다'}</small>`
      : g.resting ? ' <small>물려 둠 — 다시 부르면 그대로 선다</small>'
      : (g.active ? '' : ' <small>대기 — 지금 한도 밖이라 안 따라온다</small>');
    const rest = g.down ? ''
      : g.resting
        ? `<button onclick="act.bodyguardRest('${id}', false)">부르기</button>`
        : `<button class="ghost" onclick="act.bodyguardRest('${id}', true)">물리기</button>`;
    let up = '';
    if (g.down) {
      up = g.revive != null
        ? `<button onclick="act.bodyguardRevive('${id}', ${lv}, ${Number(g.revive) || 0})"${inTown && !(y.dead > 0) ? '' : ' disabled'}>부활 (${num(g.revive)} 은화)</button>`
        : '';
    } else if (lv >= (b.max ?? 10)) {
      up = `<small>+${lv} 끝</small>`;
    } else if (g.next) {
      const can = !!b.enh && inTown && !(y.dead > 0) && shardsHave >= g.next.shards;
      up = `<button onclick="act.bodyguardEnhance('${id}', ${lv}, ${Number(g.next.shards) || 0}, ${Number(g.next.silver) || 0})"${can ? '' : ' disabled'}>`
        + `+${lv + 1} 강화 (조각 ${num(g.next.shards)} · ${num(g.next.silver)} 은화)</button>`
        // 10-20 사용자 — 조각이 되는 만큼 한 번에 끝까지(단계마다 서버가 다시 판단)
        + ` <button onclick="act.bodyguardEnhanceAllShards('${id}')"${can ? '' : ' disabled'}>최대까지</button>`;
    }
    return `<article class="ux-card"><div class="kv"><span>${g.kind === 'ranged' ? '🏹' : '🛡️'} 보디가드 ${i + 1} <b>${lv ? `+${lv} ` : ''}${role}</b></span>`
      + `<b>${g.down ? '쓰러짐' : `HP ${g.hp}%`}</b></div>${note}`
      + `<div class="row">${rest}${g.down ? up : ''}</div>`
      + (g.down ? '' : `<details class="lore" id="guard-growth-${id}"><summary>이 동료 육성 · 현재 +${lv}</summary><div class="in"><div class="row">${up}</div>`
        + muted('강화는 확정으로 한 단계 오른다. 쓸 조각과 은화는 실행 전에 다시 확인한다.') + '</div></details>')
      + `<details class="lore" id="guard-dismiss-${id}"><summary>영구 해고</summary><div class="in">${muted('내보낸 동료와 쓴 조각은 돌아오지 않는다. 잠시 쉬게 하려면 물리기를 누른다.')}`
      + `<button class="ghost" onclick="act.bodyguardDismiss('${id}')">이 동료 내보내기</button></div></details></article>`;
  }).join('');
  const enhNote = !b.enh ? '지금은 보디가드 강화가 닫혀 있다. '
    : `<b>강화</b>는 경비병의 조각 + 은화로 확정 +1(끝: +${b.max ?? 10}) — 오를수록 능력치·사거리·시야가 오르고 겉모습이 바뀌며, +1부터는 보이는 몬스터를 스스로 찾아 친다. `
      + `강화한 보디가드는 쓰러져도 사라지지 않고 <b>단계에 비례한 은화</b>로 부활한다(은화는 가방 먼저, 모자라면 창고). `
      + (inTown ? '' : '강화·부활은 마을에서 한다. ')
      + `가방: 조각 ${num(shardsHave)}개 · ${num(silverHave)} 은화.`;
  // Q276 — 긴 안내는 한 줄 요약 + [자세히]
  return `<div class="sec"><h3>보디가드 <em>${active}/${b.cap}명${rested ? ` · 물려 둠 ${rested}명` : ''} · ${BG_WHY[b.why] || ''}</em></h3>${rows}`
    + fold('물리기는 잠시 거두기 · 내보내기는 영구 해고',
      '나를 먼저 친 사람·나를 노리는 몬스터에게 반격한다. '
      + '<b>물리기</b>는 그 한 명을 잠시 안 보이게 하는 것이고 다시 부르면 그대로 선다(한도도 안 먹는다) — '
      + '<b>내보내기</b>는 영구 해고라 쓴 조각이 안 돌아온다.<br>' + enhNote)
    + '</div>';
}

/** Read current supplies and unsold stock only; no invented expedition income or safe-return guarantee. */
function expeditionPlace(y) {
  const native = nativeFieldGeometry();
  const floor = native ? nativeFieldFloor() : floorAt(y.x, y.y);
  return floor > 0 ? floorLabel(floor) : ZONE_KO[native ? nativeFieldZone() : zoneAt(y.x, y.y)] || '';
}
export function expeditionStateKey(y) {
  return [y.weight ?? '', y.spd ?? '', y.taxPct ?? '', expeditionPlace(y)].join(':');
}
export function expeditionSummary(y, j = y?.inv?.junk ?? {}, c = y?.inv?.consum ?? {}) {
  const potions = POTIONS.filter((p) => p.id !== 'scroll');
  const pots = potions.reduce((sum, p) => sum + Math.max(0, Math.floor(Number(c[p.id]) || 0)), 0);
  const cap = Number(y.cap), weight = Number(y.weight);
  const knownWeight = Number.isFinite(cap) && cap > 0 && Number.isFinite(weight) && weight >= 0;
  const spare = knownWeight ? Math.max(0, cap - weight) : null;
  const speed = Number(y.spd), slowed = Number.isFinite(speed) && speed < 1;
  const place = expeditionPlace(y);
  const sale = junkSalePreview(y, j);
  const warnings = [];
  if (slowed) warnings.push(speed <= 0 ? '과적으로 움직일 수 없다. 소지품을 정리하고 출구를 확인해 달라.' : '과적으로 느려졌다. 전리품을 더 줍기 전에 돌아갈 길을 확인해 달라.');
  else if (knownWeight && spare === 0) warnings.push('적재 여유가 없다. 다음 전리품을 줍기 전에 정리하거나 귀환을 준비해 달라.');
  if (pots === 0) warnings.push('회복 물약이 없다. 더 진행하기 전에 보급할 곳과 돌아갈 길을 확인해 달라.');
  const advice = warnings.length ? warnings.join(' ') : '다음 적과 출구를 보고 더 진행할지, 전리품을 들고 돌아갈지 정해 달라.';
  return `<details class="lore expedition-summary"><summary>원정 준비 · 물약 ${num(pots)}개${spare === null ? '' : ` · 적재 여유 ${num(spare)}`}</summary><div class="in">`
    + (place ? `<div class="kv"><span>현재 사냥터</span><b>${place}</b></div>` : '')
    + `<div class="kv"><span>회복 물약</span><b>${potions.map((p) => `${p.name} ${num(Math.max(0, Math.floor(Number(c[p.id]) || 0)))}개`).join(' · ')}</b></div>`
    + `<div class="kv"><span>적재 여유</span><b>${spare === null ? '무게를 확인 중이다' : `${num(spare)} · 현재 ${num(weight)} / ${num(cap)}`}</b></div>`
    + (Number.isFinite(speed) ? `<div class="kv"><span>과적 이동속도</span><b>기본의 ${num(Math.round(speed * 100))}%</b></div>` : '')
    + `<div class="kv"><span>잡템 매각 시 수령 예상</span><b>${num(sale.net)} 은화</b></div>`
    + muted(`흥정·왕도 세금 ${taxPct(y)}% 반영. 아직 팔지 않은 재고이며 원정 순익은 아니다.`)
    + muted(advice) + '</div></details>';
}

export function render({ y, at, eq, j, c }) {
  const equipped = y.inv.equipped || {};
  const view = ['gear', 'items', 'stats'].includes(S.invView) ? S.invView : 'gear';
  /**
   * 보디가드는 Q276 부터 메뉴 › 나 › 동료(`companions.js`)에 있다. 전에는 가방 세 칸 어디서나 맨 위였다
   * (사용자: *"보디가드를 돌려보내거나 관리하는 메뉴는 어디있어?"* — 그때는 소지품 탭 맨 아래라 못 찾았다).
   * 이제 보디가드·펫을 「동료」 한 곳에서 보고, 가방은 가진 물건만 보인다.
   *
   * R167(레드팀 C23) — 성향 카드는 **능력 탭에만** 둔다(아래 `view === 'stats'`). 전에는 세 탭 맨 위라 가방을 열 때마다
   * 첫 화면을 「선3 · 사망 시 장비 보호」 카드가 차지하고 장비 목록이 그 아래로 밀렸다. 성향은 능력치·각성과 같은 몸의 값이다.
   */
  let h = '';
  let wornOverview = '';

  /**
   * ── **처음 오는 이에게 주는 것** (`starter`) ──
   *
   * **맨 위에 둔다.** 가방은 처음 여는 화면이고, 이걸 받아야 할 사람은
   * *"뭘 먼저 해야 하나"* 를 모르는 사람이다. 아래에 두면 못 찾는다.
   *
   * ⚠ **레벨로 감추지 않는다.** 이건 **계정 × 세계로 한 번**이라 새 세계가 열리면
   * Lv50 이던 사람도 다시 받는다. *"초보에게만"* 으로 감추면 **받을 수 있는 사람이
   * 못 받는다** — 오늘 여러 번 나온 그 부류(화면이 서버보다 엄격한 쪽)다.
   *
   * 이미 받았는지는 **화면이 모른다.** 서버가 *"이 세계에서 줄 것은 이미 주었다"*
   * 로 답한다 — 모르는 것을 아는 척 감추지 않는다.
   */
  if (view === 'items') {
    // 늘 첫 화면을 차지하던 지원품을 작은 카드로 줄인다. 버튼은 사라지지 않고 같은 자리에 있다.
    h += '<div class="starterline"><span><b>새 세계 지원품</b><small>물약 20 · 조잡한 무기 · 세계당 1회</small></span>'
      + buyBtn(S.busy === 'starter' ? '받는 중…' : '받는다', 'act.starter()',
        { y, at, need: 'starter', more: S.busy === 'starter' ? ['답을 기다린다'] : [] }) + '</div>';
  }

  /**
   * ── 착용 ──
   *
   * **칸 둘이 목록 높이의 45%를 먹고 있었다** — 눈대중이 아니라 쟀다.
   * 2열 격자에 `aspect-ratio:1` 이라 폭이 넓어질수록 칸이 같이 커진다:
   *   옛 착용칸 192px / 목록 429px = **45%**   →   지금 46px = **11%**
   * 그러면서 정작 **이름이 없어서** 무엇을 차고 있는지는 눌러야 알 수 있었다.
   * 열두 자루가 든 가방보다 두 자루가 네 배 넓은 자리를 쓰던 셈이다.
   *
   * 다른 칸과 **같은 크기**로 줄이고 이름을 옆에 적는다 — 크기로 강조하는 대신
   * 이름을 보여주는 쪽이 "무엇을 차고 있나" 라는 물음에 곧장 답한다.
   */
  /**
   * ── **인형(paper doll)** ──
   *
   * 사용자: *"그럼 인벤 창도 그에 맞게 **최신 유행하는 게임들처럼** 변화하겠지?"*
   * 그건 지어낼 것이 아니라 **표준이 있다** — 가운데 사람, 둘레에 슬롯
   * (`docs/GEAR-R2.md` 6.6). 전에는 칸 둘이 가로로 누워 있어서 **"내가 지금
   * 어떻게 생겼나" 가 아니라 "칸이 둘 있다"** 만 보였다.
   *
   * ⚠ **지금 서버에 있는 슬롯만 그린다.** 투구·장갑·부츠·망토는 아직 없다 —
   * 없는 칸을 미리 그리면 **화면이 서버보다 너그러워지고**(`places.js` 머리말 ⓑ)
   * 사람은 못 채우는 칸을 영영 쳐다본다. 슬롯이 늘면 **아래 표에 id 를 넣는 것이
   * 전부**이고, 자리는 이미 잡혀 있다.
   */
  /**
   * ⚠ **이름은 `SLOT_NAME`(미러)에서 온다.** 화면이 따로 적으면 서버가 부르는
   * 이름과 갈라지고, 그건 *"내가 낀 그것"* 인지 알 수 없게 만든다.
   * ★ 표에 없는 부위는 **자동으로 빠진다** — 자리 목록에만 적고 표에 없으면
   *   못 채우는 칸이 생기는데, 그게 *"화면이 서버보다 너그러운"* 그 부류다.
   */
  /**
   * ★ **두 열을 손으로 안 적는다 — 표에서 갈린다.**
   *
   * 전에는 `['helm','armor','cloak']` 처럼 이름을 나열했다. 그러면 **부위가 늘 때
   * 이 줄이 조용히 낡아** 새 칸이 인형에 안 나온다(`check-slots` 가 그것을 찾아낸다).
   *
   * 가르는 기준은 **그 부위가 무엇을 주는가**다 —
   * **막는 것(`def`)은 몸에 걸치고, 때리거나 움직이는 것(`atk`·`move`)은 손발**이다.
   * 지금 표로 계산하면 왼쪽 투구·갑옷·망토 / 오른쪽 무기·장갑·신발이 되어
   * 손으로 적던 것과 같고, **새 부위가 생기면 저절로 제 자리에 붙는다.**
   *
   * ⚠ 이름표(`kinds`)가 없는 부위(가방)는 인형에 안 올린다 — 가방은 따로 화면이 있다.
   */
  const worn3 = Object.keys(SLOT_KINDS).filter((id) => SLOT_META[id]);
  /**
   * UNI-Q36 — **반지 두 칸은 양손 옆**(각 열의 맨 아래), **날개 칸은 머리 위**(가운데 열의 맨 위).
   * 반지 칸 이름은 표(`SLOT_META.wears`)에서 온다 — 손으로 `['ring','ring2']` 를 적지 않는다.
   * 오른손(무기 쪽) 옆이 첫째 칸, 왼손 옆이 둘째 칸이다.
   */
  const RINGS = wearSlotsOf('ring');
  const DOLL_L = [...worn3.filter((id) => SLOT_META[id].gives === 'def'), ...RINGS.slice(1)];
  const DOLL_R = [...worn3.filter((id) => SLOT_META[id].gives !== 'def'), ...RINGS.slice(0, 1)];

  const wearCell = (slot) => {
    const e = eq.find((q) => q.id === equipped[slot]);
    return '<div class="wearone">' + (e
      ? cell({
        key: e.id, ...gearTone(e), glyph: gearGlyph(slot, e.crude ? null : e.grade || 0),
        badge: '+' + e.plus, on: true, sel: S.invSel === e.id,
        title: eqTitle(e),
      }) + `<span class="wearnm">${eqName(e)}</span>${gearTradeLabel(e, equipped)}`
      : `<div class="cell empty"><span class="ic px" style="--c:#3a3427">${gearGlyph(slot)}</span></div>`
        + `<span class="wearnm empty">${SLOT_NAME[slot]} 없음</span>`)
      + '</div>';
  };

  /**
   * 가운데 그림은 **세계에 그리는 그 사람과 같은 표에서 나온다**(`sprite`).
   * 여기서 따로 그리면 **화면 속 나와 세계 속 나가 달라진다** — 이 레포가
   * 사본으로 여러 번 데인 그 자리다. 16×16 도트를 그대로 키운다(`pixelated`).
   *
   * `toDataURL` 이 실패해도 화면이 죽지 않게 감싼다 — 인형 하나 때문에
   * 가방 전체가 안 열리면 그건 개선이 아니라 고장이다.
   */
  let me = '';
  try {
    me = `<img class="dollme${Array.isArray(y.look?.wing) ? ' winged' : ''}" alt="" src="${personSprite(y.klass, y.look, y.gender).toDataURL()}">`;
  } catch { me = '<div class="dollme"></div>'; }

  if (view === 'gear') {
    const wd = WING_BY_KEY.get(y.inv.wing);
    const left = wd ? Math.max(0, Math.ceil(((y.inv.wingCd || {})[wd.key] || 0) - Date.now() / 1000)) : 0;
    /**
     * UNI-Q36 — **날개 칸**은 인형 머리 위다. 낀 날개는 칸을 누르면 물품 상세(접기)로, 빈 칸이면
     * 가방에 든 날개를 바로 아래 줄에서 펼친다. 날개는 `inv.junk` 의 물건이라 열쇠가 `j:` 다.
     */
    const wingCell = wd
      ? '<div class="wearone dollwing">' + cell({
        key: 'j:' + wd.key, tint: wd.color, glyph: WING_GLYPH, on: true, sel: S.invSel === 'j:' + wd.key,
        title: `${wd.name} — ${left ? `재사용 ${Math.ceil(left / 60)}분` : '사용 가능'}`,
      }) + `<span class="wearnm">${wd.name}</span></div>`
      : `<div class="wearone dollwing"><div class="cell empty"><span class="ic" style="--c:#3a3427">${WING_GLYPH}</span></div>`
        + '<span class="wearnm empty">날개 없음</span></div>';
    const spareWings = WING_BY_KEY.size ? [...WING_BY_KEY.values()].filter((d) => (j[d.key] || 0) > 0 && d.key !== y.inv.wing) : [];
    wornOverview = sec('착용',
      '<div class="doll">'
      + `<div class="dollcol">${DOLL_L.map(wearCell).join('')}</div>`
      // 이름은 안 적는다 — HUD 와 세계의 이름표가 이미 말하고, 사람이 지은 문자열을
      // innerHTML 에 그대로 넣는 자리를 하나 더 만들 이유가 없다. 대신 **무엇인지**를 적는다
      + `<div class="dollmid">${wingCell}${me}<span class="qt">${y.klass === 'ranged'
        ? '원거리' : y.klass === 'assassin' ? '암살자' : '근접'}</span></div>`
      + `<div class="dollcol">${DOLL_R.map(wearCell).join('')}</div>`
      + '</div>'
      /**
       * **스탯을 출처별로 묶는다** (사용자: *"덜렁 있어서 뭔지 모르겠다 · 정렬도 안 됨 · 위치가 이상"*).
       * 세 묶음 — 기본(공격·방어·전투력) · 무기 특성(직업 고유) · 장비 옵션 합산(굴림 옵션).
       * ⚠ **`combatpair`·`dolltrait` 클래스는 그대로 둔다** — check-stat-bonuses·readability-probe 가
       * 그 문자열을 계약으로 검증한다(무기 특성 텍스트가 보이는가·doll 중심 밖인가). 라벨은
       * 겉을 감싸는 `statgroup` 헤더로 붙여 계약을 안 건드린다.
       */
      + '<div class="statgroups">'
      + '<div class="statgroup"><div class="statgrouphd">기본</div>'
      + `<div class="combatpair"><span><small>공격</small><b>${Math.round(y.atk)}</b></span>`
      + `<span><small>방어</small><b>${Math.round(y.def)}</b></span>`
      /**
       * POWER-R1 — 본인 전투력 (사용자: *"단순 레벨 말고"*).
       * ⚠ **값은 서버가 준다**(`pubSelf.power`) — 산식은 서버 `powerOf` 하나뿐이고
       * 화면이 다시 계산하면 새 스킬이 생긴 날 화면 값만 낡는다. 옛 서버라 값이
       * 없으면 칸을 안 그린다 — 없는 값을 0 으로 그리면 그게 값처럼 읽힌다.
       */
      + (Number.isFinite(y.power) ? `<span><small>전투력</small><b>${num(y.power)}</b></span>` : '')
      + '</div></div>'
      + `<div class="statgroup"><div class="statgrouphd">무기 특성</div><div class="dolltrait">${y.klass === 'ranged' ? '걸으며 쏘면 공격속도 절반' : y.klass === 'assassin' ? '기본 회피 +10% · 기본 치명 +12% · 단일 공격' : '받는 피해 -20%'}${Number.isFinite(y.attackRange) && Number.isFinite(y.criticalMult) ? `<br>공격 사거리 ${num(y.attackRange)}칸 · 치명타 피해 ${num(y.criticalMult)}배` : ''}</div></div>`
      + specialGearLine(y.gearEff)
      + '</div>'
      // 가방에 든 다른 날개 — 인형의 날개 칸에서 바로 갈아 펼친다
      + (spareWings.length ? '<div class="row wrap">' + spareWings.map((d) =>
        `<button onclick="act.equipWing('${d.key}')">${d.name} 펼치기</button>`).join('') + '</div>' : ''));
    if (y.inv.storedAcc) wornOverview += muted(`보관 중인 악세: ${ACC_BY_ID.get(y.inv.storedAcc)?.name || '기존 악세'}. 악세 칸이 비면 되돌아온다.`);
  }

  // ── 장비 ──
  // 낀 것은 칸 이름이 아니라 id 로 가린다 — 반지는 둘째 칸에 있을 수 있다(UNI-Q36)
  const wornIds = new Set(Object.values(equipped));
  const bag = eq.filter((e) => !wornIds.has(e.id));
  if (view === 'gear') {
    h += `<div class="sec"><h3>가방 속 장비 <em>${bag.length}</em></h3>`;
    h += bag.length
      ? '<div class="baggrid">' + bag.map((e) => '<div class="bagitem'
        + (S.invSel === e.id ? ' sel' : '') + '">' + cell({
          key: e.id, ...gearTone(e), glyph: gearGlyph(e.slot, e.crude ? null : e.grade || 0),
          badge: '+' + e.plus, sel: S.invSel === e.id,
          // 상인이 안 파는 등급(영웅부터)은 **칸이 통째로 빛난다**(`gearTone` 의 rare). 아이콘 색만으로는
          // 열 칸이 늘어선 격자에서 안 걸린다 — 얻고도 못 알아보면 없는 것과 같다
          title: eqTitle(e),
        }) + `<span class="baglabel">${eqName(e)}</span>${gearTradeLabel(e, equipped)}</div>`).join('') + '</div>'
      : muted('보유 장비가 없다. <b>무기·방어구 상점</b>에서 구매하거나 대장간에서 <b>강화석</b>으로 제작해 달라.');
    h += `</div>`;
    h += `<details class="lore" id="inv-worn-overview"${bag.length ? '' : ' open'}><summary>지금 착용한 장비 · 내 모습과 능력</summary><div class="in">${wornOverview}</div></details>`;
    h += autoBreakdown(y);
  }

  // ── 재료 ──
  const mats = Object.keys(j).filter((k) => (j[k] || 0) > 0);
  if (view === 'items') h += `<div class="sec"><h3>재료 <em>${mats.length}종</em></h3>` + (mats.length
    ? '<div class="baggrid">' + mats.map((k) => {
      const book = PASSIVE_BY_KEY.get(k);
      const carry = CARRY_BY_KEY.get(k);
      const wing = WING_BY_KEY.get(k);
      const imprint = SKILL_IMPRINT_BY_KEY.get(k);
      const life = k === LIFE_STONE_KEY;
      const guardShard = k === GUARD_SHARD_KEY; // UNI-Q192 — 경비병 판갑 남색
      const key = 'j:' + k;
      return `<div class="bagitem${S.invSel === key ? ' sel' : ''}">` + cell({
        key,
        // 귀한 순서대로 색이 세진다: 잡템 갈색 < 강화석 금 < 축복석 밝은 금 < 서적 자홍
        tint: wing ? wing.color : life ? '#f3e4a6' : guardShard ? '#9fb4d8' : imprint ? imprint.color : book?.rare ? '#e6a5ff' : book ? '#ff8ae0' : carry ? '#63dfc4' : k === BSTONE_KEY ? '#f0d26b' : k === SHARD_KEY ? '#d4a83c' : '#8c7853',
        glyph: wing ? WING_GLYPH : life ? LIFE_GLYPH : imprint ? IMPRINT_GLYPH : book ? BOOK_GLYPH : carry ? CARRY_GLYPH : (MAT_GLYPH[k] || MAT_GLYPH.junk_t1),
        qty: j[k],
        // 서적은 칸 자체가 빛난다 — 색만으로는 격자에서 살필 때 안 걸린다
        rare: !!book || !!carry || !!wing || !!imprint || life || guardShard,
        title: wing ? `${wing.name} ×${j[k]} — 별도 날개 슬롯`
          : life ? `생명의 돌 ×${j[k]} — 레벨 성장 · 세계 수명 봉헌 · 계정 영혼석 제작`
          : imprint ? `${imprint.name} ×${num(j[k])} — 새길 때마다 강해지는 전투 각인`
          : book ? `${book.name} ×${j[k]} — 습득 단계는 사망해도 유지 · 새 세계에서 초기화`
          : carry ? `${carry.name} ×${j[k]} — 순서대로 사용해 무게 한도 증가 · 새 세계에서 초기화`
          : `${junkName(k)} ×${j[k]}${JUNK_PRICE[k] ? ` · ${num(JUNK_PRICE[k] * j[k])} 은화` : ''}`,
      }) + `<span class="baglabel">${junkName(k)}<small>×${j[k]}</small></span></div>`;
    }).join('') + '</div>'
    : muted('비어 있다.')) + `</div>`;

  // ── 소모품 ──
  const cons = Object.keys(c).filter((k) => (c[k] || 0) > 0);
  if (view === 'items') h += `<div class="sec"><h3>소모품 <em>${cons.length}종</em></h3>` + (cons.length
    ? '<div class="baggrid">' + cons.map((k) => {
      const s = POTIONS.find((q) => q.id === k);
      const key = 'c:' + k;
      return `<div class="bagitem${S.invSel === key ? ' sel' : ''}">` + cell({
        key, tint: k === 'scroll' ? '#7fa8c9' : k === 'pot_m' ? '#e05b4a' : '#c9736c',
        glyph: k === 'scroll' ? SCROLL_GLYPH : POT_GLYPH, qty: c[k],
        title: `${s ? s.name : k} ×${c[k]}`,
      }) + `<span class="baglabel">${s ? s.name : k}<small>×${c[k]}</small></span></div>`;
    }).join('') + '</div>'
    : muted('비어 있다.')) + `</div>`;

  if (view === 'items') h += autoActions(y, c, at);
  if (view === 'stats') h += renameRow(y, at);
  if (view === 'stats') h += alignmentSummary(y.alignment) + stats(y, at);
  if (view !== 'stats') h += expeditionSummary(y, j, c);
  /**
   * 고른 칸의 상세를 **위에 고정한다** (대장간과 같은 뼈대 — `split()`).
   *
   * 원래는 "칸을 누르면 아래에 열린다" 였는데 그 아래가 폰에서는 **화면 밖**이었다.
   * 그래서 화면 바닥에 붙이는 덮개(`.actbar sheet`)로 바꿨는데, **덮개는 목록 위를
   * 덮어 클릭을 삼킨다** — 오늘 대장간에서 그 사고를 겪었다.
   *
   * 지금은 상자를 둘로 가른다: 위는 안 스크롤하고 아래만 스크롤한다.
   * 구조적으로 서로를 못 가리므로 삼킬 클릭이 없다.
   */
  const d = detail({ y, at, eq, j, c });
  /**
   * ★ **고른 것은 아래 띠로 내려갔다** (`split` 의 셋째 인자).
   *
   * 위에 고정하면 안 덮기는 하는데 **엄지에서 제일 멀다** — 실측으로
   * 칸(`y=1203`)과 버튼(`y=205`)이 1,000px 떨어져 있었다.
   * 아무것도 안 골랐을 때는 **띠가 아예 안 생긴다** — 빈 띠가 목록을 깎으면
   * 그건 자리만 먹는 것이다.
   */
  const weight = Number(y.weight || 0), cap = Math.max(1, Number(y.cap || 1));
  const wr = Math.min(100, Math.round(weight / cap * 100));
  const focus = '<div class="invhead">'
    + `<div class="invsummary"><span><small>무게</small><b class="${wr >= 100 ? 'w' : ''}">${num(weight)} / ${num(cap)}</b></span>`
    + `<span><small>은화</small><b>${num(y.silver || 0)}</b></span>`
    + `<span><small>보유</small><b>${bag.length + mats.length + cons.length}종</b></span></div>`
    + `<div class="invweight"><i style="width:${wr}%"></i></div>`
    + '<div class="invnav">'
    + `<button class="${view === 'gear' ? 'on' : ''}" onclick="act.invView('gear')">장비 <i>${bag.length}</i></button>`
    + `<button class="${view === 'items' ? 'on' : ''}" onclick="act.invView('items')">물품 <i>${mats.length + cons.length}</i></button>`
    + `<button class="${view === 'stats' ? 'on' : ''}" onclick="act.invView('stats')">능력 <i>${y.statFree || 0}</i></button>`
    + '</div></div>';
  return split(
    focus,
    h,
    S.invSel ? `<button class="ghost sheetx" onclick="act.pick('${S.invSel}')">닫기</button>${d}` : '',
  );
}

/**
 * 고른 칸의 상세 — 버튼은 여기에만 둔다.
 *
 * 머리줄을 `.fname` 으로 낸다 — **대장간과 같은 모양**이다. 고른 것의 이름이
 * 늘 같은 자리에 같은 꼴로 있어야 화면을 옮겨다녀도 눈이 안 헤맨다.
 * (검사기도 이 자리를 본다 — 레이아웃 이름이 아니라 "고르면 반응이 있는가" 를.)
 */
const fsec = (title, body) => `<div class="fname"><b>${title}</b></div>${body}`;

/**
 * 장비가 주는 몫을 줄로 그린다.
 *
 * ⚠ 이름표를 **부위**가 아니라 **몫**에 단다. 부위는 늘지만 몫의 종류는
 *    가 돌려주는 칸이 전부다 — 새 칸이 생기면 이름을 몰라도
 *   **키 그대로** 보여준다. 조용히 빠지는 것보다 낫다.
 */
const BONUS_KO = {
  atk: '공격력', def: '방어력', move: '이동속도', haste: '공격속도',
  regen: '자연회복', evade: '회피', cap: '무게 한도',
};
const PCT_BONUS = new Set(['move', 'haste', 'regen', 'evade']);
function statRows(bonus) {
  const rows = Object.entries(bonus).filter(([, v]) => v);
  if (!rows.length) return '';
  return rows.map(([k, v]) => {
    const txt = PCT_BONUS.has(k) ? '+' + (v * 100).toFixed(1) + '%' : num(Math.round(v));
    return '<div class="kv"><span>' + (BONUS_KO[k] || k) + '</span><b>' + txt + '</b></div>';
  }).join('');
}

/** 서버가 계산해 보낸 착용 굴림 합계 — 화면 미러가 실제 전투와 갈리지 않게 한다. */
/**
 * **장비 옵션 합산** 묶음 — 굴림 옵션이 붙은 착용 장비의 합. 출처 라벨(헤더)을 달고
 * 값을 우측 정렬한다(사용자: *"덜렁 있어서 뭔지 모르겠다 · 정렬 안 됨"*). 붙은 옵션이
 * 하나도 없으면 **묶음 자체를 안 그린다** — 빈 라벨은 있는 것보다 나쁘다.
 */
function specialGearLine(bonus) {
  const b = bonus || {};
  const rows = ['haste', 'regen', 'evade', 'move']
    .filter((k) => Number(b[k]) > 0)
    .map((k) => `<div class="statrow"><span>${BONUS_KO[k]}</span><b>+${(Number(b[k]) * 100).toFixed(1)}%</b></div>`);
  return rows.length
    ? `<div class="statgroup"><div class="statgrouphd">장비 옵션 합산</div>${rows.join('')}</div>`
    : '';
}

function detail({ y, at, eq, j, c }) {
  const k = S.invSel;
  if (!k) return muted('칸을 누르면 자세히 보인다.');

  if (k.startsWith('j:')) {
    const id = k.slice(2);
    const n = j[id] || 0;
    // 습득한 단계는 사망해도 남지만 세계 초기화 때는 계승하지 않는다.
    // 마을이 아니어도 읽을 수 있다: 최하층에서 얻은 것을 마을까지 들고 나와야 하면
    // 그 사이에 죽어서 잃을 위험이 생기고, "사망해도 안 잃는다" 와 어긋난다.
    const bk = PASSIVE_BY_KEY.get(id);
    if (bk) {
      // 14.514 — 단계에 끝이 없다: 늘 다음 단계가 있다(문장은 서버 값 — 옛 패킷이면 미러 Lv1~10)
      const view = y.passives?.find(p => p.id === bk.id);
      const rank = view?.rank ?? passiveRankOf(y.inv.passives, bk.id);
      const current = view?.current ?? bk.effects[rank - 1] ?? '효과 확인 중';
      const next = view?.next ?? bk.effects[rank] ?? '효과 확인 중';
      return fsec(bk.name,
        `<div class="kv"><span>현재 Lv${num(rank)}</span><b>${num(n)}권 보유</b></div>`
        + muted(rank > 0 ? `현재: ${current}` : '아직 습득하지 않았다.')
        + `<div class="kv"><span>읽은 뒤</span><b>${next}</b></div>`
        + muted('책 1권 소모 · 습득 단계는 사망해도 유지 · 새 세계에서 초기화')
        + `<div class="ux-actions"><button class="wide" onclick="act.read('${bk.id}')"${n > 0 ? '' : ' disabled'}>${rank ? `Lv${num(rank + 1)} 강화` : '읽는다'}</button></div>`
        + fold('책 획득처', passiveBookSourceText(bk)));
    }
    const imprint = SKILL_IMPRINT_BY_KEY.get(id);
    if (imprint) {
      const view = y.imprints?.find(p => p.id === imprint.id);
      const rank = view?.rank ?? skillImprintRankOf(y.inv.imprints, imprint.id);
      const equipped = y.inv.imprint === imprint.id;
      return fsec(imprint.name,
        `<div class="kv"><span>현재 Lv${num(rank)}</span><b>${num(n)}개 보유</b></div>`
        + muted(rank ? `현재: ${view?.current ?? imprint.effects[rank - 1] ?? '효과 확인 중'}` : '아직 습득하지 않았다.')
        + `<div class="kv"><span>새긴 뒤</span><b>${view?.next ?? imprint.effects[rank] ?? '효과 확인 중'}</b></div>`
        + muted('각인석 1개 소모 · 배운 단계와 남은 각인석은 사망해도 유지')
        + `<div class="ux-actions"><button class="wide" onclick="act.imprintRead('${imprint.id}')"${n > 0 ? '' : ' disabled'}>${rank ? `Lv${num(rank + 1)}로 새긴다` : '각인 습득'}</button></div>`
        + (rank ? muted(equipped ? '<b>현재 전투 각인으로 장착 중이다.</b>' : '스킬 탭에서 전투 각인으로 장착할 수 있다.') : '')
        + fold('각인석 획득처', imprint.from));
    }
    const carry = CARRY_BY_KEY.get(id);
    if (carry) {
      const rank = carryRankOf(y.inv.carryRank);
      const done = carry.rank <= rank;
      const next = carry.rank === rank + 1;
      return fsec(carry.name,
        `<div class="kv"><span>현재 단계</span><b>${rank}/5 · +${num(carryBonus(rank))}</b></div>`
        + `<div class="kv"><span>이 각인</span><b>무게 한도 +${num(carry.cap)}</b></div>`
        + `<div class="kv"><span>보유</span><b>${n}개</b></div>`
        + muted(`<b>${carry.from}</b>에게서 떨어지거나 봇짐장수에게 산다.<br>`
          + '사용하면 무게 한도가 늘어난다. 사망·재접속 후에도 유지되며, 새 세계에서는 초기화된다.')
        + (done
          ? muted('이미 새긴 단계다. 남은 것은 거래하거나 다음 사람에게 건넬 수 있다.')
          : next
            ? `<button class="wide" onclick="act.useCarry('${carry.key}')">각인 습득</button>`
            : muted(`<b>${rank + 1}단계부터</b> 차례로 새겨야 한다. 이 아이템은 소비하지 않는다.`))
        + dropRow(id, n));
    }
    const wing = WING_BY_KEY.get(id);
    if (wing) {
      const on = y.inv.wing === wing.key;
      const left = Math.max(0, Math.ceil(((y.inv.wingCd || {})[wing.key] || 0) - Date.now() / 1000));
      return fsec(wing.name,
        `<div class="kv"><span>보유</span><b>${n}개</b></div>`
        + `<div class="kv"><span>상태</span><b>${left ? `재사용 ${Math.ceil(left / 60)}분` : '사용 가능'}</b></div>`
        + muted(`${wing.desc}<br><b>악세사리와 별도 슬롯</b>이며 캐릭터 모습에 표시된다.`)
        + `<button class="wide${on ? ' sel' : ''}" onclick="${on ? 'act.unequipWing()' : `act.equipWing('${wing.key}')`}">${on ? '접는다' : '펼친다'}</button>`);
    }
    if (id === LIFE_STONE_KEY) {
      return fsec('생명의 돌', `<div class="kv"><span>보유</span><b>${n}개</b></div>`
        + lifeStoneChoices(y)
        + muted(`보스 처치나 탑 보상으로 얻을 수 있다. 마지막 날이 시작되기 전, 아무 마을에서 신전을 열어 바치면 세계 종료가 ${LIFE_STONE_SECS / 3600}시간 늦어지고, 기여자 이름이 계정 영구 비석에 남는다.<br><b>잡화상에게 팔리는 잡템이 아니다.</b>`)
        + muted(`돌 1개를 쓰면 레벨이 ${LIFE_STONE_LEVELS} 오른다. 쓴 돌은 사라진다. 마지막 날에도 쓸 수 있다.`)
        // LAUNCH-R1 — 'consumeLifeStone'은 net.js NATIVE_ACTIONS에 없어 네이티브 필드(사냥터)에서는
        // 조용히 거절된다(1.0에는 이 지역 제한이 없었다). 버튼을 잠그고 이유를 붙인다.
        + (S.lifeStoneNotice ? `<p role="status">${esc(S.lifeStoneNotice)}</p>` : '')
        + (nativeActionAllowed('consumeLifeStone')
          ? `<button class="wide" onclick="act.consumeLifeStone()" ${S.busy || y.dead > 0 ? 'disabled' : ''}>${S.busy === 'consumeLifeStone' ? '사용 중…' : '사용'}</button>`
          : `<button class="wide" disabled>사용</button>` + muted('지금 있는 사냥터에서는 쓸 수 없다 — 마을로 돌아가면 쓸 수 있다.'))
        + `<button class="ghost" onclick="act.openPanel('temple')">신전에서 봉헌·영혼석 보기</button>`
        + dropRow(id, n));
    }
    /**
     * UNI-Q192 — 경비병의 조각 → 보디가드 고용. 고용에 드는 수·한도·이유는 서버가 준 값(`y.bodyguard`)을 그린다 —
     * 화면이 운영 설정을 복제하지 않는다. 버튼은 이 칸에 보이는 수(보유·필요·한도)만으로 열리고 닫힌다.
     */
    if (id === GUARD_SHARD_KEY) {
      const b = y.bodyguard;
      const hire = b?.hire ?? 0, cap = b?.cap ?? 0, hired = b?.list?.length ?? 0;
      const can = !!b?.on && hire > 0 && n >= hire && hired < cap && !(y.dead > 0);
      const label = !b?.on ? '지금은 고용할 수 없다' : hired >= cap ? `데리고 다닐 수 있는 수(${cap}명)가 찼다`
        : n < hire ? `조각 ${hire - n}개 더 필요하다` : `보디가드 고용 (조각 ${hire}개)`;
      return fsec(GUARD_SHARD_NAME,
        `<div class="kv"><span>보유</span><b>${n}개</b></div>`
        + `<div class="kv"><span>고용에 드는 조각</span><b>${hire}개</b></div>`
        + `<div class="kv"><span>데리고 다닐 수 있는 수</span><b>${cap}명 <small>${BG_WHY[b?.why] || ''}</small></b></div>`
        + `<div class="kv"><span>지금 보디가드</span><b>${hired}명</b></div>`
        + muted('나를 공격하는 적에게 반격한다. <b>+0 동료는 쓰러지면 사라진다.</b> 새 세계에서는 초기화된다.')
        + `<div class="ux-actions"><button class="wide" onclick="act.bodyguardHire()"${can ? '' : ' disabled'}>${label}</button></div>`
        + fold('보디가드 행동과 육성', '경비병과는 싸우지 않는다. 너무 멀어지면 돌아오고 재접속하면 다시 따라온다. 강화한 보디가드는 쓰러져도 은화로 부활한다. 메뉴 → 나 → 동료에서 관리한다.')
        + dropRow(id, n));
    }
    const pardon = PARDON_BY_KEY.get(id);
    if (pardon) {
      return fsec(pardon.name,
        `<div class="kv"><span>보유</span><b>${n}개</b></div>`
        + `<div class="kv"><span>수배 보호 액면</span><b>${num(pardon.face)} 은화</b></div>`
        + muted(`누적 현상금이 <b>면죄부 액면보다 적을 때만</b> 수배 효과(전단·머리 위 표식·추격대·경비병 반응)를 막는다 — 액면 이상이면 풀린다.<br>여러 장이면 <b>최고 액면 하나만</b> 적용된다. 경비병 처형(💀)은 막지 못한다.`)
        + muted('현상금 게시판에서 <b>길드원만 명예포인트로</b> 구매할 수 있다. 새 세계에서는 사라지며, <b>사망 시 드롭</b>된다. 잡화상에게는 팔 수 없다.')
        + dropRow(id, n));
    }
    const extra = id === SHARD_KEY
      ? '대장간에서 <b>하급 장비로</b> 만들거나 <b>확률 강화</b>에 쓴다.'
      : id === BSTONE_KEY
        ? '대장간에서 쓰면 강화석과 <b>같은 확률</b>로 성공하고, 성공하면 <b>1~3 단계</b>가 한 번에 오른다. 실패해도 장비는 무사하다. <b>구역 보스</b>가 떨군다.'
        : `잡화상에게 판다. 개당 ${JUNK_PRICE[id] || 0} 은화(판매 합계에서 왕도 세금 ${taxPct(S.you)}%를 뗀다).`;
    return fsec(junkName(id),
      `<div class="kv"><span>보유</span><b>${n}개</b></div>` + muted(extra) + dropRow(id, n));
  }

  if (k.startsWith('c:')) {
    const id = k.slice(2);
    const s = POTIONS.find((q) => q.id === id);
    return fsec(s ? s.name : id,
      `<div class="kv"><span>보유</span><b>${c[id] || 0}개</b></div>${muted(s ? s.desc : '')}
       ${id !== 'scroll' ? `<button class="wide" onclick="act.use('${id}')">사용</button>` : ''}`
      + dropRow(id, c[id] || 0));
  }

  const e = eq.find((q) => q.id === k);
  if (!e) return '';
  // 낀 칸은 id 로 찾는다 — 반지는 둘째 칸(`ring2`)에 있을 수 있다(UNI-Q36). 벗기는 그 칸으로 보낸다
  const wornAt = Object.keys(y.inv.equipped || {}).find((s) => y.inv.equipped[s] === e.id);
  const on = !!wornAt;
  const special = isTempleRingItem(e);
  const tradeRule = gearTradeRestriction(e, y.inv.equipped);
  const grade = e.grade || 0;
  /**
   * ⚠⚠ **여기가 `slot === 'weapon' ? 공격력 : 방어력` 이었다** (`FM`).
   *
   * 부위가 일곱이 되면서 **장갑(공격)·신발(이동)까지 전부 「방어력」으로** 찍혔고,
   * 신발은 값 자체가 방어 공식으로 계산돼 **틀렸다.**
   * **사람은 이 숫자를 보고 무엇을 낄지 고른다.** 이름이 틀린 것보다 나쁘다.
   *
   * ★ 부위가 무엇을 주는지는 **표가 안다**(`slotBonus` — 서버 `items.ts` 의 미러).
   *   0 이 아닌 몫을 전부 그리므로 **주는 것이 늘어도 저절로 따라온다.**
   */
  const bonus = equipmentBonus(e);
  const sellable = canSellGear(e, y.inv.equipped);
  const received = num(afterTax(sellPrice(e.plus, !!e.crude, e.blessed, grade), y));
  /**
   * **팔기·보관은 이 자리에서 끝낸다.** 10-22 개편이 판매를 접힌 칸 안 「판매」 → 상점 화면 → 「팔기」로
   * 옮겨 장비 하나에 네다섯 번을 누르게 됐다(2026-10-25 사용자 신고 「하나 팔 때마다 엄청 번거로워졌어」).
   * 사냥 뒤 정리는 여러 개를 잇달아 하는 일이라, 누르면 바로 처리되고 가방이 열린 채 다음 칸으로 간다.
   * 판매는 상점과 같게 줄 오른쪽 끝에 둔다. 귀속 장비만 창고로 보낸다 — 길드 창고 이동의 진행 표시가 창고 화면에 있다.
   */
  const keep = e.guildBound != null
    ? buyBtn('보관', `act.gearWork('${e.id}','vault')`, { at, need: 'vault' })
    : buyBtn(on ? '벗고 보관' : '보관', `act.vaultEquip('${e.id}')`, { at, need: 'vault' });
  return fsec(eqName(e), `
    ${tradeRule ? `<div class="geartradedetail">${gearTradeLabel(e, y.inv.equipped)}<span>${tradeRule.detail}</span></div>` : ''}
    ${on ? '<span class="qt">착용 중</span>' + statRows(bonus) : equipmentComparison(e, y)}
    ${guildRingInfo(e, y)}${templeRingInfo(e, y)}
    <div class="ux-actions">
      ${on ? `<button onclick="act.unequip('${wornAt}')">벗기</button>`
           : `<button class="gold" onclick="act.equip('${e.id}')">이 장비 착용</button>`}
      ${keep}
      ${buyBtn('강화·합치기', `act.gearWork('${e.id}','forge')`, { at, need: 'forge' })}
      ${sellable ? sellGearBtn(e, y, at) : ''}
    </div>
    ${sellable ? muted(`팔기 금액은 왕도 세금 ${taxPct(y)}%를 뗀 실제 수령 은화다. 판 장비는 되돌릴 수 없다.`) : ''}
    <details class="lore" id="inv-gear-tools"><summary>굴림·안전수치 살펴보기</summary><div class="in">
    <div class="kv"><span>랜덤 굴림</span><span>${rollDesc(e)}</span></div>
    <div class="kv"><span>안전수치</span><b>+${e.crude ? 5 : gradeOf(grade).safe}</b></div>
    <div class="kv"><span>${e.guildBound != null ? '귀속 규칙' : special ? '신전 규칙' : '판매가'}</span><b>${e.guildBound != null ? '판매·거래·드롭·분해 불가' : special ? '판매·분해 불가 · 죽어도 안 잃는다' : `${received} <span class="qt">왕도 세금 ${taxPct(y)}% 공제</span>`}</b></div>
    ${!e.crude && e.rolled !== 0 ? `<div class="row">${buyBtn('굴림 재추첨', `act.gearWork('${e.id}','alchemy')`, { at, need: 'alchemyReroll' })}</div>` : ''}
    </div></details>`);
}


/**
 * 스탯 — **가방에 상설로** 둔다.
 * 남은 포인트가 있을 때만 뜨는 알림 하나로는 "어디서 찍는지" 를 못 찾는다.
 * 레벨이 이미 높은 캐릭터는 포인트가 잔뜩 쌓여 있는데 그걸 볼 자리가 없었다 (사용자 지적).
 */
/**
 * 능력치 줄의 효과 한마디 — 서버 문장(`statEff`)의 첫 마디를 그대로 쓴다(수를 다시 계산하지 않는다).
 * 첫 마디에 이름이 없는 둘(힘 「+212.1%」·체력 「+125.9%」)만 무엇이 오른 값인지 머리말을 붙인다.
 */
const EFF_LEAD = { str: '공격', con: '최대 HP' };
export function statLead(key, sentence) {
  if (!sentence) return '';
  const first = String(sentence).split(' · ')[0].replace(/\s*\(.*$/, '').trim();
  return /^[+-]/.test(first) && EFF_LEAD[key] ? `${EFF_LEAD[key]} ${first}` : first;
}

/** 이름과 비용은 현재 몸의 서버 화면값이다. 옛 서버의 누락값을 무료로 취급하지 않는다. */
export function renameCost(y) {
  return Number.isSafeInteger(y?.renameCost) && y.renameCost > 0 ? y.renameCost : null;
}

function renameRow(y, at) {
  const cost = renameCost(y);
  const more = [];
  if (cost === null) more.push('변경 비용 확인 중');
  if (!S.connected || S.ws?.readyState !== 1) more.push('연결 확인 필요');
  if (y.dead > 0) more.push('부활한 뒤 가능');
  return '<div class="item">'
    + `<span class="nm">닉네임 <b>${esc(y.name || '')}</b>`
    + (S.renameNotice ? `<span class="qt" role="status">${esc(S.renameNotice)}</span>` : '') + '</span>'
    + (S.renameBusy
      ? '<button onclick="act.renameRetry()">결과 다시 확인</button>'
      : buyBtn(cost === null ? '이름 바꾸기' : `이름 바꾸기 ${num(cost)} 은화`, 'act.rename()', {
        y, at, need: 'rename', price: cost ?? 0, more,
      })) + '</div>';
}

function stats(y, at) {
  // 표는 world/map.js 하나뿐이다 (서버 stats.ts 의 미러).
  // 여기와 skillbar.js 에 손으로 적어뒀더니 검사 항목을 셋에서 여섯으로 늘리는 순간
  // 두 곳이 따로 낡았다. 화면이 설명하는 효과와 서버가 계산하는 효과가 다르면
  // 사용자는 원인을 영원히 못 찾는다.
  const st = y.stats || {};
  const free = y.statFree || 0;
  const spent = Math.max(0, Number(y.statSpent) || STATS.reduce((n, d) => n + (Number(st[d.key]) || 0), 0));
  const resetCost = Math.max(0, Number(y.statResetCost) || 0);
  const autoStats = y.inv?.autoStats === true;
  /**
   * 2026-10-21 사용자 신고 — *"내가 현재 몇을 찍었는지 가독성이 떨어지고 설명이 너무 장황해.. 논문 쓰니?"*.
   * 한 줄에 한 능력치: 이름 · **찍은 점수(크게)** · 지금 효과 한마디 · [+1][+7]. 남은 포인트는 맨 위에 크게.
   * 효과 한마디는 서버 문장(`statEff`)의 첫 마디를 그대로 쓴다 — 계수를 여기서 다시 계산하지 않는다
   * (미러는 반드시 낡고, 상한을 모르는 화면은 서버와 다른 말을 한다 — 26점에서 멈추는 값을 29점까지 그렸던 사고).
   * 다음 1점·상한 넘친 몫·회피·반지·집·레벨 성장 같은 규칙 문장은 [자세히] 안으로 접는다(지우지 않는다).
   * 버튼을 못 누르는 이유(포인트 없음·자동 배분 중)는 줄마다 되풀이하지 않고 맨 위에 한 번 적는다.
   * 스탯은 서버에 자리 판정이 없다 — 어디서나 찍는다(서버가 남은 포인트·자동 배분을 다시 판정한다).
   */
  const bonusOf = (key) => (Number(y.gearStats?.[key]) || 0) + (Number(y.houseEffects?.allStats) || 0);
  const lock = autoStats ? '자동 배분 중 — 끄면 직접 찍는다' : free ? '' : '남은 포인트 없음 — 레벨업하면 받는다';
  const plusBtn = (d, n) =>
    `<button ${autoStats || free < n ? 'disabled' : ''} onclick="act.stat('${d.key}',${n})" aria-label="${d.name} +${n}">+${n}</button>`;
  const rows = STATS.map((d) => {
    const bonus = bonusOf(d.key);
    // 왼쪽 칸 = 이름(+보너스) 위 · 효과 한마디 아래, 가운데 = 찍은 점수(크게), 오른쪽 = +1 +7.
    // 점수가 「12,500」처럼 길어져도 효과 칸이 잘리지 않게 효과를 이름 아래에 둔다(375px 실측).
    return `<div class="statline"><span class="statinfo"><span class="statname">${d.name}${bonus > 0 ? `<small>보너스 +${num(bonus)}</small>` : ''}</span>`
      + `<span class="stateff">${statLead(d.key, y.statEff?.[d.key])}</span></span>`
      + `<b class="statpts">${num(st[d.key] || 0)}</b>`
      + `<span class="statbtns">${plusBtn(d, 1)}${plusBtn(d, 7)}</span></div>`;
  }).join('');
  /**
   * [자세히] — 서버가 보낸 문장 전부. 14.482 지금 효과 전체·다음 1점(`statNext`)·상한 넘친 몫, 회피(`evadeInfo`·`evadeRule`),
   * UNI-Q36 반지(`gearStats` — 받은 이에게만 온다), 집 투자, 레벨 성장. 14.492 무릎 위 로그는 「완만하게 오른다(상한 없음)」 한 마디로.
   */
  const detail = muted('효과는 1점마다 오른다. 아주 높은 능력치에서는 완만하게 오른다(상한 없음). 상한에 닿은 효과는 넘친 몫이 다른 효과로 간다.')
    + STATS.map((d) => (y.statEff?.[d.key] || y.statNext?.[d.key])
      ? `<div class="statdetail"><b>${d.name}</b> ${y.statEff?.[d.key] ?? ''}${y.statNext?.[d.key] ? `<small>다음 1점: ${y.statNext[d.key]}</small>` : ''}</div>` : '').join('')
    + (y.evadeInfo ? `<div class="statdetail"><b>회피</b> ${y.evadeInfo}${y.evadeRule ? `<small>${y.evadeRule}</small>` : ''}</div>` : '')
    + (Number(y.templeRing?.worn) > 0
      ? `<div class="statdetail"><b>반지</b> ${y.gearStats
        ? `모든 능력치 +${num(Math.max(...STATS.map((d) => Number(y.gearStats[d.key]) || 0)))}`
        : '반지 획득 후 공개'}</div>` : '')
    + (Number(y.houseEffects?.allStats) > 0 || Number(y.houseEffects?.evade) > 0
      ? `<div class="statdetail"><b>현재 집 투자 혜택</b> 모든 능력치 +${num(y.houseEffects.allStats || 0)} · 회피 +${num((y.houseEffects.evade || 0) * 100)}%p</div>` : '')
    + levelGrowthSummary(y.levelGrowth, false);
  return '<div class="sec statpanel">'
    + `<div class="stattop"><span class="statfree"><small>남은 포인트</small><b>${num(free)}</b></span>`
    + `<span class="statspent"><small>찍은 합계</small><b>${num(spent)}</b></span></div>`
    + (lock ? `<div class="statlock">${lock}</div>` : '')
    + autoStatsRow(y)
    + `<div class="statlines">${rows}</div>`
    + fold('능력치 효과 자세히', detail)
    + `<div class="item statitem"><span class="nm">스탯 초기화
        <span class="qt">찍은 ${num(spent)}점을 모두 돌려받는다.</span></span>
        ${buyBtn(`초기화 ${num(resetCost)} 은화`, `act.statReset(${spent},${resetCost})`, {
          y, at, need: 'statReset', price: resetCost,
          more: spent > 0 ? [] : ['투자 스탯 없음'],
        })}</div>`
    + awakenRow(y, at)
    // 관리자 전용. 우상단에 따로 창을 띄우던 것을 여기로 합쳤다 —
    // 같은 표를 두 곳에 그리면 한쪽이 반드시 낡고, 화면도 그만큼 좁아진다.
    + ((y.operator || y.admin) ? '<div class="item"><span class="nm">관리 계정</span>'
      + '<button onclick="act.grant(100)">보너스 +100</button></div>' : '')
    + '</div>';
}
/**
 * ── **자동 물약** (사용자 지시: *"자동으로 물약을 먹는 기능 넣을래?"*) ──
 *
 * 판정은 서버가 한다(`do.ts` 의 `autoPotion`). 화면은 **설정을 보내고 상태를 보여줄
 * 뿐**이다 — 하드코어라 클라가 "마셔라" 를 정하면 그건 곧 치트다.
 *
 * ⚠ **문턱은 사용자가 정한다.** 우리가 고르는 값이 아니라고 못박혔다.
 *
 * ★ **켜져 있는데 물약이 없으면 그 사실을 말한다.**
 *   오늘 P0 이 정확히 그 부류였다 — `hud.js` 가 *"버리거나 창고에 맡겨라"* 라고
 *   말하는데 버릴 수단이 0건이었다. **켜두고 안심했는데 조용히 죽는 것**이
 *   이 기능에서 같은 얼굴이다.
 *
 * ⚠ 10~90 은 서버 `AUTO_POT_MIN/MAX` 와 같아야 한다. **지금은 손으로 맞춘 값이고
 *   검사 항목이 없다** — 어긋나도 서버가 잘라내므로 사고는 안 나지만, 화면이 못 고르는
 *   값을 보여주거나 그 반대가 된다. `check-client` 의 상수 대조에 걸어야 한다(65 인계).
 */
/**
 * **각성 — 영혼석**(UNI-Q222 · 사다리 개편 UNI-Q227 · DESIGN 14.293·14.295).
 *
 * 값은 전부 서버가 준 것을 쓴다(`y.soul`) — 사다리·남은 양·재료를 화면이 다시 계산하면
 * 서버와 갈라지고, 갈라진 화면은 **사람에게 거짓말**을 한다.
 * ⚠ 되돌릴 수 없는 교환이라 두 번 묻는다 — 버튼 한 번에 일생이 사라지면 안 된다.
 * ⚠ **단계에 끝이 없다** — 「최고 단계 완료」 상태는 없다.
 * ⚠ **지금 받는 능력치만 그린다**(UNI-Q231 사용자 지시 — 간결하게) — 다음 단계·진행률·그때의 능력치는 화면 어디에도 안 나오고 서버도 안 보낸다.
 * ⚠ **경험치 숫자는 안 그린다**(UNI-Q230) — 서버가 안 보낸다.
 */
/** The same three stone uses are compared in the bag and temple. Configured soul costs come from the server. */
export const soulCostKnown = (soul) => Number.isSafeInteger(soul?.lifeCost) && soul.lifeCost >= 1
  && Number.isSafeInteger(soul?.silverCost) && soul.silverCost >= 0;
export function lifeStoneChoices(y) {
  const soul = y.soul;
  return '<div class="life-stone-choices">'
    + `<div class="kv"><span>내가 사용 · 돌 1개</span><b>레벨 +${LIFE_STONE_LEVELS}</b></div>`
    + `<div class="kv"><span>신전에 봉헌 · 돌 1개</span><b>현재 세계 수명 +${LIFE_STONE_SECS / 3600}시간</b></div>`
    + `<div class="kv"><span>계정 영혼석 제작</span><b>${soul?.made ? '이미 제작했다' : !soulCostKnown(soul) ? '교환 조건을 확인 중이다' : `생명의 돌 ${num(soul.lifeCost)}개 · ${num(soul.silverCost)} 은화`}</b></div>`
    + muted('레벨은 즉시 오르고 · 봉헌은 모두의 시간을 늘리고 · 영혼석은 일생의 경험치로 키운다')
    + '</div>';
}

export function awakenRow(y, at) {
  const s = y.soul;
  if (!s) return '';
  const rank = Number(s.rank) || 0;
  const allStats = Math.round(s.bonuses?.allStats ?? 0);
  const line = rank > 0
    ? `영혼석 +${rank} — 최대 체력·공격력·방어력 +${num(Math.round((s.bonuses?.maxHp ?? 0) * 100))}% · 회피 +${num(Math.round((s.bonuses?.evade ?? 0) * 100))}%p · 모든 능력치 +${num(allStats)}`
    : '캐릭터가 아니라 영혼을 키운다.';
  return `<div class="item statitem"><span class="nm">각성 ${rank > 0 ? `<em>+${rank}</em>` : ''}
        <span class="qt">${line}</span>
        <span class="qt">Lv${num(s.offerLevel ?? y.level)} 일생의 경험치를 전부 투자한다. 레벨·찍은 스탯·스킬 강화는 처음으로 돌아간다. 장비·강화·집은 남으며 세계마다 한 번만 바칠 수 있다.</span></span>`
    + (s.made
      ? buyBtn(`일생을 바친다 (Lv${num(y.level)} 일생어치)`, `act.awakenFeed(${Number(y.level) || 0})`, {
        y, at, need: 'awakenFeed',
        // 세계마다 한 번(UNI-Q230) — 이미 바쳤으면 버튼이 이유를 말하고 잠긴다. 서버가 다시 판정한다
        more: s.done ? ['이 세계는 이미 각성했다 — 세계마다 한 번'] : Number(y.level) > 1 ? [] : ['바칠 경험치 없음'],
      })
      : !soulCostKnown(s) ? muted('영혼석 교환 조건을 확인 중이다.') : buyBtn(`영혼석 만들기 (생명의 돌 ${num(s.lifeCost)} · ${num(s.silverCost)} 은화)`, 'act.awakenForge()', {
        y, at, need: 'awakenForge', price: Number(s.silverCost) || 0,
        more: (Number(y.inv?.junk?.lifestone) || 0) >= Number(s.lifeCost) ? [] : [`생명의 돌 ${num(s.lifeCost)}개 필요`],
      }))
    + '</div>';
}

/** 추천 스탯 자동 배분 켬끔 — 가방 능력 화면과 설정 화면(UNI-Q173)이 같은 줄을 쓴다 */
export function autoStatsRow(y) {
  const autoStats = y.inv?.autoStats === true;
  const target = AUTO_STAT_TARGETS[y.klass] || AUTO_STAT_TARGETS.melee;
  // 2026-10-21 — 한 줄로: 이름·직업 비율(서버 목표 표 미러 `AUTO_STAT_TARGETS`)·켬끔 스위치. 「누르면 켠다」 같은 말은 스위치가 이미 한다.
  const ratio = STATS.map((s) => `${s.name} ${Math.round(target[s.key] * 100)}%`).join(' · ');
  return `<div class="item statitem autostat"><span class="nm">추천 자동 배분
        <span class="qt">${ratio}</span></span>
        <button class="${autoStats ? 'sel' : ''}" role="switch" aria-checked="${autoStats ? 'true' : 'false'}" onclick="act.autoStats(${autoStats ? 'false' : 'true'})">${autoStats ? '켜짐' : '꺼짐'}</button></div>`;
}

export function autoActions(y, c, at) {
  const a = (y.inv && y.inv.autoPot) || { on: false, pct: 0.5 };
  // 값이 없는 기존 캐릭터도 켜짐이다. 서버와 같은 기본값이어야 배포 전 캐릭터만
  // 기능이 꺼지는 일이 없다.
  const wear = !y.inv || y.inv.autoEquip !== false;
  const retaliate = y.inv?.autoRetaliate === true;
  const pct = Math.round((a.pct ?? 0.5) * 100);
  // 회복 물약만 센다 — 귀환 주문서는 마셔도 체력이 안 는다
  const heal = POTIONS.filter((q) => q.id !== 'scroll').reduce((n, q) => n + (c[q.id] || 0), 0);

  /**
   * 2026-10-21 「논문 쓰니?」 — 설정 창(settings.js UI2b)과 같은 한 줄: 이름·짧은 설명(왼쪽) + 상태를 쓴 스위치(오른쪽).
   * 전에는 카드마다 상태 글자(「켜짐」)와 「켜짐 — 누르면 끈다」 버튼이 같은 말을 두 번 했고, 카드 밑에 설명 문단이 한 줄씩 더 붙었다.
   */
  const sw = (on, onclick) =>
    `<button class="set-switch${on ? ' sel' : ''}" role="switch" aria-checked="${on ? 'true' : 'false'}" onclick="${onclick}">${on ? '켜짐' : '꺼짐'}</button>`;
  const row = (title, sub, body, extra = '') =>
    `<div class="auto-card set-row"><span class="set-label"><b>${title}</b><small>${sub}</small></span>${body}${extra}</div>`;
  return sec('자동 동작',
    '<div class="auto-grid set-list">'
    + row('자동 착용', '확실히 더 좋은 장비만 · 쓰던 것은 가방에 남는다', sw(wear, `act.autoEquip(${wear ? 'false' : 'true'})`))
    + row('자동 반격', '나를 친 몬스터·경비병·플레이어를 잠시 쫓는다', sw(retaliate, `act.autoRetaliate(${retaliate ? 'false' : 'true'})`))
    + row('자동 줍기', '밟은 물건을 줍는다 · 무게가 넘치면 바닥에 둔다', '<b class="set-state">항상 켜짐</b>')
    + row('자동 강화', '대장간에서 장비·목표를 고른다', buyBtn('자동 강화 열기', 'act.autoForge()', { at, need: 'forge' }))
    + row('자동 물약', `<b data-autopot-pct>HP ${pct}%</b> 아래로 떨어지면 마신다`, sw(a.on, `act.autoPot(${a.on ? 'false' : 'true'}, ${a.pct})`),
      `<div class="row set-note"><input id="apct" type="range" min="10" max="90" step="5" value="${pct}" aria-label="자동 물약 HP 문턱"`
      + ` oninput="act.autoPotPreview(this.value)" onchange="act.autoPot(${a.on}, this.value/100)"></div>`
      + (a.on && heal === 0
        ? '<small class="set-note lack"><b>물약이 없다</b> — 물약상인에게서 회복 물약을 사 둔다</small>'
        : a.on ? `<small class="set-note">회복 물약 ${heal}개 · 작은 것부터</small>` : ''))
    + '</div>');
}

/** 장비 탭에서 바로 찾는 저장형 자동 분해 문턱. 기존 가방은 건드리지 않는다. */
export function autoBreakdown(y) {
  const raw = Number(y.inv?.autoBreakdownGrade);
  const grade = Number.isInteger(raw) && raw >= 0 && raw < GRADES.length ? raw : -1;
  const label = grade < 0 ? '꺼짐' : `조잡 포함 ${GRADES[grade].name} 이하`;
  return sec('자동 분해',
    '<div class="auto-card"><div class="kv"><span><b>새로 주운 +0 장비</b>'
    + '<small>기준 등급 이하면 그 자리에서 재료로 바꾼다</small></span>'
    + `<b>${label}</b></div>`
    + '<select class="wide" aria-label="자동 분해 등급" onchange="act.autoBreakdown(this.value)">'
    + `<option value="-1"${grade < 0 ? ' selected' : ''}>자동 분해 끄기</option>`
    + GRADES.map((g) => `<option value="${g.id}"${grade === g.id ? ' selected' : ''}>${g.name} 이하</option>`).join('')
    + '</select>'
    // 10-21 운영 신고 「자동분해가 안 된다」 — 실측: 남은 +0 63/64 가 기준보다 높은 등급(「희귀 이하」에 최고급 등). 그 사실을 줄에 적는다
    + muted(`${grade >= 0 && grade < GRADES.length - 1 ? `${GRADES[grade + 1].name} 이상은 가방에 남는다 · ` : ''}자동 착용한 장비·착용품·+1 이상은 보호 · 이미 가방에 있는 것은 그대로`)
    + '</div>');
}

/**
 * ── **버리기** ──
 *
 * 오늘 P0 의 마무리다. `hud.js` 가 과적일 때 *"버리거나 창고에 맡겨라"* 라고
 * 말했는데 **버릴 수단이 코드에 0건**이었다 — 창고는 마을에 있고, 과적이면
 * 못 움직여서 마을에 갈 수가 없다. **말은 하는데 그대로 못 하면 사용자가
 * 자기를 의심한다.**
 *
 * ⚠ **바닥에 둔다. 없애지 않는다.** 서버가 `putDrop` 으로 떨구므로 도로 주울 수
 * 있다 — 하드코어에서 되돌릴 수 없는 버튼은 만들지 않는다.
 * ⚠ 장비는 여기서 안 버린다. 그쪽은 분해·판매가 따로 있고, 겸하면 규칙이 두 벌이 된다
 * (서버도 같은 이유로 잡템·소모품만 받는다).
 */
function dropRow(id, have) {
  if (have < 1) return '';
  /**
   * R143(레드팀 H15) — 사냥터(네이티브 필드)가 `drop` 을 받지 않던 때는 버튼 대신 귀환 길을 적었다.
   * 묶음 X2(T9, DESIGN 14.443) — 이제 사냥터도 버리기를 받는다(net.js NATIVE_ACTIONS · 러스트 `propose_discard`, 즉시 확정 저장).
   * 아래 분기는 허용 목록에서 다시 빠질 때만 탄다(버튼이 거절만 받는 일이 없게).
   */
  if (!nativeActionAllowed('drop')) return muted(`사냥터에서는 버리지 않는다. 무거우면 귀환(${keyCap(ACTION_KEYS.scroll)})해 마을에서 팔거나 창고에 맡겨 달라.`);
  const half = Math.max(1, Math.floor(have / 2));
  return `<details class="lore"><summary>버리기 · 누구나 주울 수 있음</summary><div class="in"><div class="row">`
    + `<button onclick="act.drop('${id}', 1)">1개 버리기</button>`
    + (have > 1 ? `<button onclick="act.drop('${id}', ${half})">${half}개</button>` : '')
    + (have > 1 ? `<button onclick="act.drop('${id}', ${have})">전부</button>` : '')
    + `</div>` + muted('바닥에 버린 아이템은 <b>누구나 주울 수 있다.</b>') + '</div></details>';
}
